/* Created by Interactive 63 - https://interactive63.com */
/**
 * Conversion of property values into CSS declarations, following the mapping in the property
 * registry documentation.
 */
import { normaliseColour } from '../colour';
import { formatNumber } from '../easing/format-number';
import type { PropertyKey, PropertyValue } from '../model/types';
import { isDrawValue, isVec2 } from '../model/types';

export interface ValueContext {
  /** Total path length of the element, needed by stroke reveal. */
  pathLength?: number;
}

/** Path length assumed when an element's length could not be measured. */
export const FALLBACK_PATH_LENGTH = 1000;

/** CSS properties each animatable property writes, used to clear conflicting inline styles. */
export const CSS_PROPERTIES: Record<PropertyKey, string[]> = {
  translate: ['translate'],
  rotate: ['rotate'],
  scale: ['scale'],
  skew: ['transform'],
  opacity: ['opacity'],
  fill: ['fill'],
  stroke: ['stroke'],
  strokeWidth: ['stroke-width'],
  fillOpacity: ['fill-opacity'],
  strokeOpacity: ['stroke-opacity'],
  draw: ['stroke-dasharray', 'stroke-dashoffset'],
  blur: ['filter'],
  stopColor: ['stop-color'],
  stopOpacity: ['stop-opacity'],
  morph: [],
  fontSize: ['font-size'],
  // Words switch between generated spans, which carry their own rules.
  words: [],
};

/** Formats a number with up to three decimals. */
export function num(value: number): string {
  return formatNumber(value, 3);
}

/** Converts a camelCase property key to kebab case, for keyframe names. */
export function kebab(property: string): string {
  return property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

/** Returns the CSS declarations (without trailing semicolons) for a property value. */
export function cssDeclarations(
  property: PropertyKey,
  value: PropertyValue,
  context: ValueContext = {},
): string[] {
  switch (property) {
    case 'translate':
      return isVec2(value) ? [`translate: ${num(value.x)}px ${num(value.y)}px`] : [];
    case 'rotate':
      return typeof value === 'number' ? [`rotate: ${num(value)}deg`] : [];
    case 'scale':
      return isVec2(value) ? [`scale: ${num(value.x)} ${num(value.y)}`] : [];
    case 'skew':
      return isVec2(value) ? [`transform: skew(${num(value.x)}deg, ${num(value.y)}deg)`] : [];
    case 'opacity':
    case 'fillOpacity':
    case 'strokeOpacity':
    case 'stopOpacity':
      return typeof value === 'number' ? [`${kebab(property)}: ${num(value)}`] : [];
    case 'fill':
    case 'stroke':
    case 'stopColor':
      return typeof value === 'string' ? [`${kebab(property)}: ${normaliseColour(value)}`] : [];
    case 'strokeWidth':
      return typeof value === 'number' ? [`stroke-width: ${num(value)}px`] : [];
    case 'blur':
      return typeof value === 'number' ? [`filter: blur(${num(value)}px)`] : [];
    case 'fontSize':
      return typeof value === 'number' ? [`font-size: ${num(Math.max(0, value))}px`] : [];
    case 'words':
      return [];
    case 'draw': {
      if (!isDrawValue(value)) return [];
      const length = context.pathLength ?? FALLBACK_PATH_LENGTH;
      const visible = Math.max(0, ((value.end - value.start) / 100) * length);
      return [
        `stroke-dasharray: ${num(visible)}px ${num(length)}px`,
        `stroke-dashoffset: ${num((-value.start / 100) * length)}px`,
      ];
    }
    case 'morph':
      // Shape morphing is driven by the runtime, not CSS.
      return [];
  }
}
